import { unwrapData } from '@/utils/unwrap';
import { useQuery } from '@tanstack/react-query';
import { subscriptionApi } from '@/api/subscription.api';
import type { SubscriptionStatus } from '@/api/subscription.api';
import { useAuth } from '@/contexts/AuthContext';

export function useSubscription() {
  const { clinic } = useAuth();
  const clinicId = clinic?.id || '';

  const { data: subscription, isLoading: subscriptionLoading } = useQuery({
    queryKey: ['subscription', clinicId],
    queryFn: () => subscriptionApi.getClinicSubscription(),
    enabled: !!clinicId,
  });

  const { data: limits, isLoading: limitsLoading } = useQuery({
    queryKey: ['subscription-limits', clinicId],
    queryFn: () => subscriptionApi.getPlanLimits(),
    enabled: !!clinicId,
  });

  const { data: bookingAccess, isLoading: bookingLoading } = useQuery({
    queryKey: ['booking-access', clinicId],
    queryFn: () => subscriptionApi.checkBookingAccess(),
    enabled: !!clinicId,
  });

  const {
    data: subscriptionStatus,
    isLoading: statusLoading,
    isError: statusIsError,
    isSuccess: statusIsSuccess,
  } = useQuery({
    queryKey: ['subscription-status', clinicId],
    queryFn: () => subscriptionApi.getSubscriptionStatus(),
    enabled: !!clinicId,
    refetchInterval: 60 * 60 * 1000, // Refetch every hour
    // A 401 here just means the access token expired while idle; the client
    // interceptor refreshes and the retry succeeds. Retry a couple of times so
    // a transient auth/network blip never resolves to a "blocked" state.
    retry: 2,
  });

  // Handle both wrapped { data: ... } and direct response formats
  const resolvedLimits = unwrapData(limits);
  const resolvedBookingAccess = unwrapData(bookingAccess);
  const resolvedStatus: SubscriptionStatus | undefined =
    unwrapData(subscriptionStatus);

  return {
    subscription,
    limits: resolvedLimits,
    hasBookingApp: resolvedBookingAccess?.hasBookingApp ?? false,
    isLoading:
      subscriptionLoading || limitsLoading || bookingLoading || statusLoading,
    canAddBranch: (currentCount: number) =>
      resolvedLimits ? currentCount < resolvedLimits.maxBranches : true,
    canAddRoom: (currentCount: number) =>
      resolvedLimits ? currentCount < resolvedLimits.maxRoomsPerBranch : true,
    // Subscription status
    subscriptionStatus: resolvedStatus,
    // Only treat the clinic as blocked when a status request has actually
    // succeeded and confirmed it. If the query is loading, retrying, or errored
    // (e.g. a 401 during the idle token-refresh window), we must NOT report
    // blocked — otherwise the user sees a false "subscription expired" page.
    isBlocked: statusIsSuccess ? (resolvedStatus?.isBlocked ?? false) : false,
    // Surface the error so the guard can keep showing the app (not the expired
    // page) while a transient failure resolves.
    statusError: statusIsError,
    isInGracePeriod: resolvedStatus?.isInGracePeriod ?? false,
    showExpirationWarning: resolvedStatus?.showWarning ?? false,
    daysUntilExpiration: resolvedStatus?.daysUntilExpiration ?? 0,
    daysUntilGraceEnds: resolvedStatus?.daysUntilGraceEnds ?? 0,
  };
}
